import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { StackedBars, LineChart, Legend } from "../COMPONENTS/HistoryCharts";
import {
  HistActivity, HistWellness, SPORT_GROUPS, hours, monthShort, shiftYears,
  buildWeeks, buildMonths, buildPeriods, yearOverlay, monthlyWellness,
  buildHighlights, rollingMean,
} from "../COMPONENTS/trainingHistory";
import "../CSS/trainingHistory.scss";
import { usePageBackground } from "../COMPONENTS/usePageBackground";

const YEAR_COLORS = ["#5b8def", "#3aa76d", "#d08a1f", "#e2b31a", "#e0503f"];
const INTENSITY = [
  { label: "Lätt (Z1–2)", color: "#4aa3df" },
  { label: "Medel (Z3)", color: "#e2b31a" },
  { label: "Hårt (Z4+)", color: "#e0503f" },
];

const fmtH = (h: number) => `${Math.round(h)} h`;
const fmtNum = (n: number) => Math.round(n).toLocaleString("sv-SE");
const fmtDate = (s: string) =>
  new Date(s + "T12:00:00").toLocaleDateString("sv-SE", { day: "numeric", month: "short", year: "numeric" });

// "jan 24" on the first period of each quarter, null elsewhere.
const quarterLabel = (month: number, year: number) =>
  month % 3 === 0 ? `${monthShort(month)} ${String(year).slice(2)}` : null;

interface Props {
  data: { activities: HistActivity[]; wellness: HistWellness[] } | null;
  years: number;
}

export function TrainingHistoryView({ data, years }: Props) {
  usePageBackground();
  const view = useMemo(() => {
    if (!data) return null;
    const today = new Date().toISOString().slice(0, 10);
    const from = shiftYears(today, -years);
    const { activities, wellness } = data;
    const weeks = buildWeeks(activities, from, today);
    const months = buildMonths(activities, from, today);
    const periods = buildPeriods(activities, today, years);
    const wm = monthlyWellness(wellness, months);
    const sortedWellness = [...wellness].sort((a, b) => (a.id < b.id ? -1 : 1));
    return {
      today, from, weeks, months, periods, wm, sortedWellness,
      overlay: yearOverlay(months, from, today),
      highlights: buildHighlights(activities, wellness, weeks, months, today),
      weeklyHours: weeks.map((w) => hours(w.secs)),
    };
  }, [data, years]);

  return (
    <div className="hist">
      <div className="hist__header">
        <NavLink to={routeUrls.TRAINING} className="hist__back">← Träning</NavLink>
        <h1 className="hist__title">Översikt · {years} år</h1>
        <span />
      </div>

      {!view ? (
        <p className="hist__nodata">Laddar…</p>
      ) : view.weeks.every((w) => w.sessions === 0) ? (
        <p className="hist__nodata">Inga aktiviteter hittades de senaste {years} åren.</p>
      ) : (
        <div className="hist__body">
          {/* ── Period cards ─────────────────────────────────────────── */}
          <section>
            <h2 className="hist__h">År för år</h2>
            <p className="hist__sub">Rullande 12-månadersperioder fram till idag.</p>
            <div className="hist__cards">
              {view.periods.map((p, k) => {
                const prev = view.periods[k + 1];
                const delta = prev && prev.secs > 0 ? Math.round(((p.secs - prev.secs) / prev.secs) * 100) : null;
                const lmh = p.low + p.mid + p.high;
                return (
                  <div key={p.label} className="hist__card">
                    <div className="hist__card-label">{p.label}</div>
                    <div className="hist__card-range">{p.range}</div>
                    <div className="hist__card-big">
                      {fmtH(hours(p.secs))}
                      {delta != null && (
                        <span className={`hist__delta hist__delta--${delta >= 0 ? "up" : "down"}`}>
                          {delta >= 0 ? "+" : ""}{delta} %
                        </span>
                      )}
                    </div>
                    <dl className="hist__stats">
                      <div><dt>Pass</dt><dd>{p.sessions}</dd></div>
                      <div><dt>Timmar/vecka</dt><dd>{(hours(p.secs) / p.weeks).toFixed(1)}</dd></div>
                      <div><dt>Distans</dt><dd>{fmtNum(p.km)} km</dd></div>
                      <div><dt>Höjdmeter</dt><dd>{fmtNum(p.elev)} m</dd></div>
                      <div><dt>Belastning (TSS)</dt><dd>{fmtNum(p.tss)}</dd></div>
                      <div><dt>Hård andel</dt><dd>{lmh > 0 ? `${Math.round((p.high / lmh) * 100)} %` : "–"}</dd></div>
                    </dl>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Weekly volume ────────────────────────────────────────── */}
          <section>
            <h2 className="hist__h">Veckovolym</h2>
            <p className="hist__sub">Timmar per vecka fördelat på sport. Linjen är 8-veckors glidande snitt.</p>
            <StackedBars
              cols={view.weeks.map((w) => ({
                tip: `Vecka från ${fmtDate(w.start)}: ${hours(w.secs).toFixed(1)} h, ${w.sessions} pass`,
                parts: w.bySport.map(hours),
              }))}
              colors={SPORT_GROUPS.map((g) => g.color)}
              xLabels={view.weeks.map((w, i) => {
                const m = Number(w.start.slice(5, 7)) - 1;
                const prevM = i > 0 ? Number(view.weeks[i - 1].start.slice(5, 7)) - 1 : -1;
                return m !== prevM ? quarterLabel(m, Number(w.start.slice(0, 4))) : null;
              })}
              line={rollingMean(view.weeklyHours, 8)}
              unit=" h"
            />
            <Legend items={SPORT_GROUPS.map((g) => ({ label: g.label, color: g.color }))} />
          </section>

          {/* ── Fitness / fatigue ────────────────────────────────────── */}
          <section>
            <h2 className="hist__h">Form: kondition och trötthet</h2>
            <p className="hist__sub">CTL (kondition, långsam) och ATL (trötthet, snabb) från intervals.icu.</p>
            <LineChart
              series={[
                { label: "Kondition (CTL)", color: "#3aa76d", values: view.sortedWellness.map((d) => d.ctl) },
                { label: "Trötthet (ATL)", color: "#e0503f", values: view.sortedWellness.map((d) => d.atl), dashed: true },
              ]}
              xLabels={view.sortedWellness.map((d, i) => {
                const prev = i > 0 ? view.sortedWellness[i - 1].id.slice(0, 7) : "";
                return d.id.slice(0, 7) !== prev ? quarterLabel(Number(d.id.slice(5, 7)) - 1, Number(d.id.slice(0, 4))) : null;
              })}
              zero
            />
            <Legend items={[{ label: "Kondition (CTL)", color: "#3aa76d" }, { label: "Trötthet (ATL)", color: "#e0503f" }]} />
          </section>

          {/* ── Year overlay ─────────────────────────────────────────── */}
          <section>
            <h2 className="hist__h">Säsong: månad för månad</h2>
            <p className="hist__sub">Timmar per månad, ett kalenderår per linje. Innevarande månad är ofullständig.</p>
            <LineChart
              series={view.overlay.map((o, i) => ({ label: String(o.year), color: YEAR_COLORS[i % YEAR_COLORS.length], values: o.values }))}
              xLabels={Array.from({ length: 12 }, (_, m) => monthShort(m))}
              tips={Array.from({ length: 12 }, (_, m) => monthShort(m))}
              unit=" h"
              zero
              dots
            />
            <Legend items={view.overlay.map((o, i) => ({ label: String(o.year), color: YEAR_COLORS[i % YEAR_COLORS.length] }))} />
          </section>

          {/* ── Sport split ──────────────────────────────────────────── */}
          <section>
            <h2 className="hist__h">Sportfördelning</h2>
            <p className="hist__sub">Andel av träningstiden per period.</p>
            <div className="hist__splits">
              {view.periods.map((p) => (
                <div key={p.label} className="hist__split">
                  <div className="hist__split-label">{p.label}</div>
                  <div className="hist__split-bar">
                    {SPORT_GROUPS.map((g, gi) => {
                      const pct = p.secs > 0 ? (p.bySport[gi] / p.secs) * 100 : 0;
                      return pct > 0 ? (
                        <div
                          key={g.key}
                          style={{ width: `${pct}%`, background: g.color }}
                          title={`${g.label}: ${hours(p.bySport[gi]).toFixed(0)} h (${Math.round(pct)} %)`}
                        >
                          {pct >= 9 ? `${Math.round(pct)} %` : ""}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              ))}
            </div>
            <Legend items={SPORT_GROUPS.map((g) => ({ label: g.label, color: g.color }))} />
          </section>

          {/* ── Intensity ────────────────────────────────────────────── */}
          <section>
            <h2 className="hist__h">Intensitetsfördelning</h2>
            <p className="hist__sub">Andel tid i lätta, medelhårda och hårda pulszoner per månad.</p>
            <StackedBars
              cols={view.months.map((m) => {
                const tot = m.low + m.mid + m.high;
                const pct = (v: number) => (tot > 0 ? (v / tot) * 100 : 0);
                return {
                  tip: `${monthShort(m.month)} ${m.year}: ${Math.round(pct(m.low))} / ${Math.round(pct(m.mid))} / ${Math.round(pct(m.high))} % (lätt/medel/hårt)`,
                  parts: [pct(m.low), pct(m.mid), pct(m.high)],
                };
              })}
              colors={INTENSITY.map((i) => i.color)}
              xLabels={view.months.map((m) => quarterLabel(m.month, m.year))}
              fixedMax={100}
              unit=" %"
            />
            <Legend items={INTENSITY} />
          </section>

          {/* ── Body signals ─────────────────────────────────────────── */}
          <section>
            <h2 className="hist__h">Kroppssignaler</h2>
            <p className="hist__sub">Månadssnitt. Sjunkande vilopuls och stigande HRV brukar följa bättre form.</p>
            <div className="hist__trio">
              {([
                { title: "Vilopuls", unit: " bpm", color: "#e0503f", values: view.wm.restingHR },
                { title: "HRV", unit: " ms", color: "#3aa76d", values: view.wm.hrv },
                { title: "Vikt", unit: " kg", color: "#4aa3df", values: view.wm.weight },
              ]).map((s) => (
                <div key={s.title} className="hist__mini">
                  <div className="hist__mini-title">{s.title}</div>
                  <LineChart
                    series={[{ label: s.title, color: s.color, values: s.values }]}
                    xLabels={view.months.map((m) => quarterLabel(m.month, m.year))}
                    tips={view.months.map((m) => `${monthShort(m.month)} ${m.year}`)}
                    height={140}
                    unit={s.unit}
                    dots
                  />
                </div>
              ))}
            </div>
          </section>

          {/* ── Highlights ───────────────────────────────────────────── */}
          <section>
            <h2 className="hist__h">Höjdpunkter</h2>
            <div className="hist__highlights">
              {view.highlights.peakCtl && (
                <div><b>{Math.round(view.highlights.peakCtl.v)}</b><span>Högsta kondition (CTL)</span><em>{fmtDate(view.highlights.peakCtl.date)}</em></div>
              )}
              {view.highlights.bigWeek && (
                <div><b>{view.highlights.bigWeek.hours.toFixed(1)} h</b><span>Största veckan</span><em>från {fmtDate(view.highlights.bigWeek.start)}</em></div>
              )}
              {view.highlights.bigMonth && (
                <div><b>{Math.round(view.highlights.bigMonth.hours)} h</b><span>Största månaden</span><em>{monthShort(Number(view.highlights.bigMonth.key.slice(5, 7)) - 1)} {view.highlights.bigMonth.key.slice(0, 4)}</em></div>
              )}
              {view.highlights.longest && (
                <div><b>{view.highlights.longest.hours.toFixed(1)} h</b><span>Längsta passet</span><em>{fmtDate(view.highlights.longest.date)}</em></div>
              )}
              <div><b>{view.highlights.weeksTrainedPct} %</b><span>Veckor med träning</span><em>av {view.weeks.length} veckor</em></div>
              <div><b>{view.highlights.longestBreakDays} dagar</b><span>Längsta uppehållet</span><em>utan pass</em></div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
